import type { TVResultMatch } from '@/utils/tv-mode-data';
import { useTVScale } from './TVScaleContext';
import { TVTeamAvatarStack } from './TVTeamAvatarStack';

const splitPlayers = (teamName: string): string[] => {
  if (!teamName) return [''];
  const parts = teamName.split(/\s*\/\s*/).filter(Boolean);
  return parts.length > 0 ? parts : [teamName];
};

/** Sets como tokens: "6  4  6" → ["6","4","6"] */
const splitScoreSets = (score: string): string[] => {
  if (!score || score === '-') return [score || '-'];
  return score.split(/\s+/).filter(Boolean);
};

interface Props {
  result: TVResultMatch;
}

/**
 * Banner "NUEVO RESULTADO" — reemplaza el ticker durante unos segundos cuando
 * llega un resultado recién cerrado. Diseño D1: panel navy/naranja con bloque
 * GANADOR a la izquierda y card del resultado a la derecha.
 *
 * Datos: consumimos `TVResultMatch` directamente — `team1Won` define ganador y
 * perdedor. Usamos `font-gilroy` (fuente base del Modo TV) en lugar de la
 * Barlow Condensed del mockup, alineado con `EmptyStateGrupos/Eliminatorias`.
 */
export const NewResultBanner = ({ result }: Props) => {
  const { s } = useTVScale();

  const winnerIsTeam1 = result.team1Won;
  const winnerName = winnerIsTeam1 ? result.team1 : result.team2;
  const loserName = winnerIsTeam1 ? result.team2 : result.team1;
  const winnerSlots = winnerIsTeam1 ? result.team1Slots : result.team2Slots;
  const loserSlots = winnerIsTeam1 ? result.team2Slots : result.team1Slots;
  const winnerScore = winnerIsTeam1 ? result.team1Score : result.team2Score;
  const loserScore = winnerIsTeam1 ? result.team2Score : result.team1Score;
  const winnerPlayers = splitPlayers(winnerName);
  const loserPlayers = splitPlayers(loserName);
  const winnerSetTokens = splitScoreSets(winnerScore);
  const loserSetTokens = splitScoreSets(loserScore);

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        borderRadius: 12,
        background:
          'linear-gradient(110deg, #011F52 0%, #023373 50%, #FF5A1D 145%)',
        animation: 'nrSlideIn 0.45s ease both',
      }}
    >
      {/* Halo naranja arriba-derecha */}
      <div
        style={{
          position: 'absolute',
          right: -30,
          top: -30,
          width: 160,
          height: 160,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(255,90,29,.5) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />
      {/* Patrón a cuadros sutil */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.06,
          backgroundImage:
            'linear-gradient(45deg, #fff 25%, transparent 25%, transparent 75%, #fff 75%), linear-gradient(45deg, #fff 25%, transparent 25%, transparent 75%, #fff 75%)',
          backgroundSize: '18px 18px',
          backgroundPosition: '0 0, 9px 9px',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: s(18),
          padding: `${s(12)}px ${s(18)}px`,
          minWidth: 0,
        }}
      >
        {/* Zona izquierda: eyebrow + GANADOR + nombres */}
        <div
          style={{
            flexShrink: 0,
            position: 'relative',
            minWidth: s(220),
            maxWidth: s(280),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: s(6),
              marginBottom: s(4),
            }}
          >
            <div
              style={{
                width: s(8),
                height: s(8),
                borderRadius: '50%',
                background: '#FFC95A',
                animation: 'nrBlink 1s infinite',
              }}
            />
            <span
              style={{
                fontSize: s(11),
                fontWeight: 800,
                color: '#FFC95A',
                letterSpacing: '0.14em',
              }}
            >
              NUEVO RESULTADO
            </span>
          </div>
          <div
            className="font-gilroy"
            style={{
              fontSize: s(13),
              fontWeight: 700,
              color: 'rgba(255,255,255,.65)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: s(2),
            }}
          >
            Ganador
          </div>
          <div
            className="font-gilroy"
            style={{
              fontSize: s(22),
              fontWeight: 900,
              color: '#fff',
              letterSpacing: '0.01em',
              lineHeight: 1.05,
              textTransform: 'uppercase',
            }}
          >
            {winnerPlayers.map((p, i) => (
              <div
                key={`w-${i}`}
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {p}
              </div>
            ))}
          </div>
        </div>

        {/* Zona derecha: card del resultado */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <div
            style={{
              background: '#fff',
              border: '1.5px solid #fff',
              borderRadius: 10,
              padding: `${s(8)}px ${s(14)}px ${s(9)}px`,
              minWidth: s(340),
              maxWidth: '100%',
              boxShadow: '0 1px 4px rgba(2,51,115,.06)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: s(5),
                marginBottom: s(5),
              }}
            >
              <span
                style={{
                  fontSize: s(11),
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  color: '#FF5A1D',
                  textTransform: 'uppercase',
                }}
              >
                ✓ {result.category}
              </span>
            </div>

            {/* Fila ganador */}
            <ResultRow
              players={winnerPlayers}
              slots={winnerSlots}
              setTokens={winnerSetTokens}
              walkover={
                winnerIsTeam1 ? !!result.team1Walkover : !!result.team2Walkover
              }
              nameColor="#023373"
              scoreColor="#FF5A1D"
              nameWeight={700}
              scoreWeight={900}
            />

            {/* Fila perdedor */}
            <div style={{ marginTop: s(5) }}>
              <ResultRow
                players={loserPlayers}
                slots={loserSlots}
                setTokens={loserSetTokens}
                walkover={
                  winnerIsTeam1
                    ? !!result.team2Walkover
                    : !!result.team1Walkover
                }
                nameColor="#8FA8C0"
                scoreColor="#C5D5E8"
                nameWeight={500}
                scoreWeight={800}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ResultRowProps {
  players: string[];
  slots: TVResultMatch['team1Slots'];
  setTokens: string[];
  walkover: boolean;
  nameColor: string;
  scoreColor: string;
  nameWeight: number;
  scoreWeight: number;
}

const ResultRow = ({
  players,
  slots,
  setTokens,
  walkover,
  nameColor,
  scoreColor,
  nameWeight,
  scoreWeight,
}: ResultRowProps) => {
  const { s } = useTVScale();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: s(10),
        minWidth: 0,
      }}
    >
      <TVTeamAvatarStack slots={slots} size={s(22)} />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          lineHeight: 1.2,
        }}
      >
        {players.map((p, i) => (
          <span
            key={`p-${i}`}
            style={{
              fontSize: s(12),
              fontWeight: nameWeight,
              color: nameColor,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textDecoration: walkover ? 'line-through' : 'none',
              textDecorationThickness: walkover ? 2 : undefined,
            }}
          >
            {p}
          </span>
        ))}
      </div>
      <div
        className="font-space"
        style={{
          display: 'flex',
          gap: s(6),
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        {setTokens.map((tok, i) => (
          <span
            key={`s-${i}`}
            style={{
              fontSize: s(24),
              fontWeight: scoreWeight,
              color: scoreColor,
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.01em',
            }}
          >
            {tok}
          </span>
        ))}
      </div>
    </div>
  );
};
